import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, ArrowRight, MessageCircle, Zap, 
  TrendingUp, Clock, Users, Target, Award,
  BarChart3, Factory, Brain, Megaphone
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBER = "33644692901";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Bonjour ! Je souhaite organiser une formation IA dans mon entreprise.")}`;

const FunnelFormationPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Formation IA en Entreprise | Académie IA - Résultats dès le premier jour</title>
        <meta name="description" content="Vos équipes utilisent réellement l'IA dès demain. Formation opérationnelle avec résultats mesurables. Devis gratuit en 2 min." />
        <meta property="og:title" content="Formation IA opérationnelle en entreprise" />
        <meta property="og:description" content="Vos équipes utilisent réellement l'IA dès demain. Pas dans 6 mois. Pas en théorie. Dès la formation." />
        <link rel="canonical" href="https://academie-ia.fr/formation-ia" />
      </Helmet>

      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#050505] to-purple-900/20" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 py-20 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight"
          >
            Vos équipes utilisent{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              réellement l'IA
            </span>{" "}
            dès demain
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl md:text-2xl text-gray-300 mb-8"
          >
            Pas dans 6 mois. Pas en théorie. <strong className="text-white">Dès la formation.</strong>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-10 max-w-2xl mx-auto"
          >
            <p className="text-lg text-gray-300 mb-4">En une journée, vos collaborateurs :</p>
            <ul className="space-y-3 text-left">
              {[
                "automatisent des tâches concrètes",
                "créent leurs premiers assistants IA",
                "gagnent du temps mesurable"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-cyan-400 font-medium mb-8"
          >
            Formation IA opérationnelle, directement applicable à vos métiers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/tunnel"
              className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105"
            >
              <Zap className="w-6 h-6" />
              Demander un devis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-500/25"
            >
              <MessageCircle className="w-6 h-6" />
              WhatsApp direct
            </a>
          </motion.div>
        </div>
      </section>

      {/* Le Problème */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-red-400 font-bold text-sm uppercase tracking-wider">Le problème</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-6">
              Aujourd'hui, 80% des formations IA ne produisent{" "}
              <span className="text-red-400">aucun impact réel</span>.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 mb-8"
          >
            <p className="text-xl text-white font-medium mb-6">Pourquoi ?</p>
            <ul className="space-y-4">
              {[
                "Trop de théorie",
                "Aucun lien avec vos process internes",
                "Aucun passage à l'action"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-300 text-lg">
                  <span className="text-red-400">✗</span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-xl text-white"
          >
            <strong>Résultat :</strong>{" "}
            <span className="text-gray-400">vos équipes s'informent mais n'utilisent pas.</span>
          </motion.p>
        </div>
      </section>

      {/* Notre Approche */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Notre approche</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-4">
              Nous ne formons pas à l'IA.
            </h2>
            <p className="text-2xl md:text-3xl text-cyan-400 font-bold">
              Nous la faisons utiliser.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 mb-8"
          >
            <p className="text-xl text-white font-medium mb-6">Pendant la formation :</p>
            <ul className="space-y-4">
              {[
                "vos équipes travaillent sur leurs propres cas",
                "elles manipulent les outils en direct",
                "elles repartent avec des solutions déjà en place"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-300 text-lg">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center space-y-2"
          >
            <p className="text-xl text-white">Ce n'est pas une formation.</p>
            <p className="text-2xl text-cyan-400 font-bold">C'est un point de bascule opérationnel.</p>
          </motion.div>
        </div>
      </section>

      {/* Preuves Business */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900/50 to-[#050505]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-400 font-bold text-sm uppercase tracking-wider">Preuves business</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 flex items-center justify-center gap-3">
              <BarChart3 className="w-10 h-10 text-amber-400" />
              Gains mesurables
            </h2>
            <p className="text-gray-400 mt-2">(moyenne observée)</p>
          </motion.div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { stat: "-30% à -60%", desc: "de temps sur tâches rédactionnelles" },
              { stat: "x2 à x5", desc: "vitesse de production de contenu" },
              { stat: "3 à 10 tâches", desc: "automatisées par collaborateur dès J1" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center"
              >
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 mb-2">
                  {item.stat}
                </div>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Cas clients */}
          <div className="space-y-8">
            {/* PME Industrielle */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Factory className="w-8 h-8 text-blue-400" />
                <div>
                  <h3 className="text-xl font-bold text-white">Cas client — PME industrielle</h3>
                  <p className="text-gray-400">120 salariés</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-red-400 font-medium mb-3">Avant :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• devis réalisés en 2h</li>
                    <li>• reporting manuel</li>
                    <li>• dépendance à 2 personnes clés</li>
                  </ul>
                </div>
                <div>
                  <p className="text-green-400 font-medium mb-3">Après 1 journée :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• devis générés en 20 minutes</li>
                    <li>• reporting automatisé</li>
                    <li>• 6 collaborateurs autonomes sur les outils IA</li>
                  </ul>
                </div>
              </div>
              <p className="mt-6 text-cyan-400 font-bold text-lg">ROI en moins de 2 semaines</p>
            </motion.div>

            {/* Cabinet de conseil */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Brain className="w-8 h-8 text-purple-400" />
                <h3 className="text-xl font-bold text-white">Cas client — Cabinet de conseil</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-red-400 font-medium mb-3">Avant :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• production de livrables lente</li>
                    <li>• forte charge mentale</li>
                    <li>• difficulté à scaler</li>
                  </ul>
                </div>
                <div>
                  <p className="text-green-400 font-medium mb-3">Après :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• assistants IA internes créés</li>
                    <li>• production divisée par 3</li>
                    <li>• capacité à prendre +40% de missions</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Équipe marketing */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Megaphone className="w-8 h-8 text-amber-400" />
                <div>
                  <h3 className="text-xl font-bold text-white">Cas client — Équipe marketing</h3>
                  <p className="text-gray-400">15 personnes</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-red-400 font-medium mb-3">Avant :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• création de contenu plafonnée</li>
                    <li>• dépendance aux freelances</li>
                  </ul>
                </div>
                <div>
                  <p className="text-green-400 font-medium mb-3">Après :</p>
                  <ul className="space-y-2 text-gray-300">
                    <li>• production interne x3</li>
                    <li>• réduction des coûts externes de 50%</li>
                    <li>• chaîne de contenu automatisée</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Les Formateurs */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Les formateurs</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-2">
              Pas des formateurs.
            </h2>
            <p className="text-2xl md:text-3xl text-cyan-400 font-bold">Des opérateurs.</p>
          </motion.div>

          <div className="space-y-6 mb-12">
            {/* Jean-Baptiste */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-start"
            >
              <div className="relative w-24 h-24 flex-shrink-0 group cursor-pointer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/eucy2b90_IMG_3268.jpeg"
                  alt="Jean-Baptiste Giraud"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 transition-opacity duration-300 group-hover:opacity-0"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/aawkwtpf_IMG_3338.jpeg"
                  alt="Commander Giraud - IA Task Force"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Jean-Baptiste Giraud</h3>
                <p className="text-gray-300">
                  Entrepreneur, développe entre 3 et 5 apps par semaine avec l'IA.
                  <br />
                  <span className="text-cyan-400">A formé dirigeants, COMEX et institutions publiques.</span>
                </p>
              </div>
            </motion.div>

            {/* Thierry */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-start"
            >
              <div className="relative w-24 h-24 flex-shrink-0 group cursor-pointer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/nagb2cvz_0871139a-c59e-4242-88b5-1c9dfc36e39d.jpeg"
                  alt="Thierry Assonion"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 transition-opacity duration-300 group-hover:opacity-0"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/6vp22mpl_IMG_3336.jpeg"
                  alt="Commander Assonion - IA Task Force"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Thierry Assonion</h3>
                <p className="text-gray-300">
                  Ancien officier supérieur, expert du renseignement.
                  <br />
                  <span className="text-purple-400">Spécialiste des signaux faibles et de l'analyse stratégique.</span>
                </p>
              </div>
            </motion.div>

            {/* Thibaut */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-start"
            >
              <div className="relative w-24 h-24 flex-shrink-0 group cursor-pointer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/ec798rh2_668f3d1a-e302-4ad9-8aad-8c15a24d1c65.jpeg"
                  alt="Thibaut Milewski"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 transition-opacity duration-300 group-hover:opacity-0"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/9okb1sr7_IMG_3337.jpeg"
                  alt="IA Rookie Milewski - IA Task Force"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Thibaut Milewski</h3>
                <p className="text-gray-300">
                  Expert OSINT et simulation.
                  <br />
                  <span className="text-amber-400">Intervient sur la collecte et l'exploitation de données réelles.</span>
                </p>
              </div>
            </motion.div>

            {/* Cécile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-start"
            >
              <div className="relative w-24 h-24 flex-shrink-0 group cursor-pointer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/xje1ackb_4.png"
                  alt="Cécile Tardy"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 transition-opacity duration-300 group-hover:opacity-0"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_01e865c2-013e-4444-9089-dd388040a505/artifacts/r7ndrwtx_IMG_3335.jpeg"
                  alt="Major Tardy - IA Task Force"
                  className="w-24 h-24 rounded-xl object-cover absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Cécile Tardy</h3>
                <p className="text-gray-300">
                  Doctorante en droit, directrice de projets et experte en stratégie digitale.
                  <br />
                  <span className="text-pink-400">25 ans d'accompagnement de dirigeants. Spécialiste ingénierie pédagogique et monétisation IA.</span>
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center space-y-2"
          >
            <p className="text-lg text-white">Ils utilisent l'IA tous les jours.</p>
            <p className="text-xl text-cyan-400 font-bold">Ils ne transmettent que ce qui fonctionne.</p>
          </motion.div>
        </div>
      </section>

      {/* Les 3 Niveaux */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-gray-900/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Les 3 niveaux</span>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                level: "Niveau 1", 
                title: "Utiliser", 
                desc: "Vos équipes maîtrisent les outils du quotidien",
                color: "from-green-500 to-emerald-500"
              },
              { 
                level: "Niveau 2", 
                title: "Automatiser", 
                desc: "Elles créent leurs propres assistants IA",
                color: "from-blue-500 to-cyan-500"
              },
              { 
                level: "Niveau 3", 
                title: "Transformer", 
                desc: "Elles repensent leurs process avec l'IA",
                color: "from-purple-500 to-pink-500"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-10 rounded-2xl`} />
                <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 h-full">
                  <div className={`inline-block px-3 py-1 bg-gradient-to-r ${item.color} rounded-full text-white text-sm font-bold mb-4`}>
                    {item.level}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-xl text-cyan-400 font-bold"
          >
            En 1 journée : passage du niveau 1 au niveau 2
          </motion.p>
        </div>
      </section>

      {/* Formats */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Formats</span>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                title: "Formation équipe", 
                features: ["1 journée", "cas réels", "résultats immédiats"],
                icon: Users,
                color: "cyan"
              },
              { 
                title: "Formation dirigeants", 
                features: ["vision stratégique", "cas personnalisés", "décision rapide"],
                icon: Award,
                color: "purple"
              },
              { 
                title: "Accompagnement", 
                features: ["déploiement IA", "structuration interne", "montée en compétence durable"],
                icon: TrendingUp,
                color: "amber"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all"
              >
                <item.icon className={`w-10 h-10 text-${item.color}-400 mb-4`} />
                <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                <ul className="space-y-2">
                  {item.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Différence */}
      <section className="py-16 px-4 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border-y border-cyan-500/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Différence</span>
            <p className="text-xl md:text-2xl text-gray-300 mt-4">
              La majorité des formations expliquent l'IA.
            </p>
            <p className="text-2xl md:text-3xl text-white font-bold mt-2">
              Nous créons de l'<span className="text-cyan-400">impact mesurable</span> en entreprise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
              Réservez votre formation
            </h2>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Link
                to="/tunnel"
                className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-xl font-bold rounded-2xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105"
              >
                <Zap className="w-7 h-7" />
                Devis en 2 minutes
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-10 py-5 bg-green-500 hover:bg-green-600 text-white text-xl font-bold rounded-2xl transition-all shadow-lg shadow-green-500/25"
              >
                <MessageCircle className="w-7 h-7" />
                WhatsApp direct
              </a>
            </div>

            <p className="text-gray-400 mb-8">réponse sous 24h</p>

            <p className="text-xl text-cyan-400 font-bold">
              Vos équipes peuvent être opérationnelles dès cette semaine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10 bg-black/50">
        <p className="text-gray-500 text-sm">
          Cette page a été <span className="text-cyan-400 font-semibold">entièrement générée par l'IA</span> avec{" "}
          <a 
            href="https://emergent.sh" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-cyan-400 hover:text-cyan-300 underline"
          >
            Emergent
          </a>
        </p>
        <p className="text-gray-600 text-xs mt-2">
          Académie IA © {new Date().getFullYear()}
        </p>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-6 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full shadow-lg shadow-green-500/30 hover:scale-110 transition-all"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden sm:inline">Contactez-nous</span>
      </a>
    </div>
  );
};

export default FunnelFormationPage;
